import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail?.data ?? {};
    const resource = event.detail?.resource;
    let resourceCount = null;
    if (resource && Array.isArray(resource.items)) {
      resourceCount = resource.items.reduce((s, line) => s + (Number(line.quantity) || 0), 0);
    } else if (resource && typeof resource.item_count === 'number') {
      resourceCount = resource.item_count;
    }
    const dataCount = typeof data.itemCount === 'number' ? data.itemCount : null;
    const count = resourceCount !== null ? resourceCount : dataCount ?? 0;

    await this.renderCartBubble(count);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Total units in cart (absolute).
   * @param {boolean} [animate=true] - Whether to animate this instance’s bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const display = itemCount < 100 ? String(itemCount) : '';

    document.querySelectorAll('[data-testid="cart-bubble"]').forEach((countEl) => {
      countEl.textContent = display;
      countEl.classList.toggle('hidden', itemCount === 0);
      const bubble = countEl.closest('.cart-bubble');
      if (bubble) bubble.classList.toggle('visually-hidden', itemCount === 0);
    });

    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: display,
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
